import { CircularProgress, makeStyles, TextField } from "@material-ui/core";
import { useContext, useEffect, useState } from "react";
import mainContext from "../../context/main/mainContext";
import ViewGroupModal from "./ViewGroupModal";
import eachLimit from "../../utilities/eachLimit";
import { axiosInstance, axiosParagonInstance } from "../../utilities/axios.config";
import ViewMemberModal from "./ViewMemberModal";

const useStyles = makeStyles(() => ({
  searchContainer: {
    display: "flex",
    paddingTop: 10,
    marginBottom: 30,
  },
}));

function Overview({ isFetchingGroups, selectedConnection }) {
  const classes = useStyles();
  const context = useContext(mainContext);
  const [groupModalOpen, setGroupModalOpen] = useState({ isOpen: false, data: {} });
  const [totalMembers, setTotalMembers] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMember, setSearchMember] = useState("");
  const [searchMemberErrorText, setSearchMemberErrorText] = useState("");
  const [searchMemberData, setSearchMemberData] = useState({});

  const { adGroups, hrMappingConf } = context;

  const { integrationSyncConfig } = selectedConnection;

  // TO DO: show admin status if setAdminFromHR is true in sync config
  const getAdmins = async (token) => {
    let globalAdmins = null;
    let groupAdmins = null;

    const directoryRoles = await axiosParagonInstance.get(
      "/directoryRoles",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ConsistencyLevel: "eventual",
        },
      },
    );
    const globalAdminRole = directoryRoles?.data?.value?.find(
      ({ displayName }) => displayName === "Global Administrator",
    );
    const groupAdminRole = directoryRoles?.data?.value?.find(
      ({ displayName }) => displayName === "Groups Administrator",
    );
    if (globalAdminRole) {
      globalAdmins = await axiosParagonInstance.get(
        `/directoryRoles/${globalAdminRole.id}/members`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ConsistencyLevel: "eventual",
          },
        },
      );
    }
    if (groupAdminRole) {
      groupAdmins = await axiosParagonInstance.get(
        `/directoryRoles/${groupAdminRole.id}/members`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ConsistencyLevel: "eventual",
          },
        },
      );
    }

    return { globalAdmins, groupAdmins };
  };

  async function getMembersGroups(userId, token) {
    let groups = [];
    let url = `/users/${userId}/memberOf`;
    let headers = {
      Authorization: `Bearer ${token}`,
      ConsistencyLevel: "eventual",
    };

    while (url) {
      const { data } = await axiosParagonInstance.get(url, { headers });
      groups.push(...data.value);
      if (data["@odata.nextLink"]) {
        const skipToken = data["@odata.nextLink"].split("skiptoken=")[1];
        url = `/users/${userId}/memberOf?$skiptoken=${skipToken}`;
      } else {
        url = null;
      }
    }
    return groups;
  }

  const handleSearch = async () => {
    try {
      if (searchMemberErrorText) setSearchMemberErrorText("");
      setIsSearching(true);
      const { data: tokenResp } = await axiosInstance.post("/internal/hr-sync/get_token", { uid: selectedConnection.uid });
      // console.log(token);
      const { data: user } = await axiosParagonInstance.get(`/users/${searchMember}?$select=${hrMappingConf.msMemberKeys}`, {
        headers: {
          Authorization: `Bearer ${tokenResp.token}`,
          ConsistencyLevel: "eventual",
        },
      });
      const { data: groups } = await axiosParagonInstance(`/users/${searchMember}/checkMemberGroups`, {
        headers: {
          Authorization: `Bearer ${tokenResp.token}`,
          ConsistencyLevel: "eventual",
        },
        method: "POST",
        data: {
          groupIds: adGroups.map(({ id }) => id),
        },
      });

      const memberGroups = await getMembersGroups(user.id, tokenResp.token);

      setIsSearching(false);
      setSearchMemberData({ user, groups: groups.value.map((id) => adGroups.find((adGroup) => adGroup.id === id)), allGroups: memberGroups });
    } catch (e) {
      setIsSearching(false);
      if (e?.response?.status === 404) {
        setSearchMemberErrorText("User not found");
      } else if (e?.response?.data?.error?.message) {
        setSearchMemberErrorText(e.response.data.error.message);
      } else {
        setSearchMemberErrorText("Internal Server Error");
      }
    }
  };

  const handleCloseMemberModal = () => {
    setSearchMemberData({});
    setSearchMember("");
  };

  useEffect(() => {
    if (adGroups?.length) {
      (async () => {
        let count = 0;
        const { data } = await axiosInstance.post("/internal/hr-sync/get_token", { uid: selectedConnection.uid });
        await eachLimit(adGroups, 5, async ({ id }) => {
          const members = await axiosParagonInstance.get(`/groups/${id}/transitiveMembers/?$count=true`, {
            headers: {
              Authorization: `Bearer ${data.token}`,
              ConsistencyLevel: "eventual",
            },
          });
          count += members.data["@odata.count"];
        });
        setTotalMembers(count);
      })();
    }
  }, [adGroups]);

  return (
    <div>
      <h4>Main info</h4>
      <table>
        <tbody>
          <tr>
            <td>Sync schedule</td>
            <td>{integrationSyncConfig.syncSchedule}</td>
          </tr>
          <tr>
            <td>Last sync date</td>
            <td>{new Date(integrationSyncConfig.lastSync).toString()}</td>
          </tr>
          <tr>
            <td>Fields to sync</td>
            <td>{integrationSyncConfig.syncFields}</td>
          </tr>
          <tr>
            <td>Fields to overwrite</td>
            <td>{integrationSyncConfig.overwriteFields}</td>
          </tr>
          <tr>
            <td>Delete members</td>
            <td>{String(!!integrationSyncConfig.isDeleteMember)}</td>
          </tr>
          <tr>
            <td>Send invites</td>
            <td>{String(integrationSyncConfig.sendInvites)}</td>
          </tr>
          <tr>
            <td>Sync all cards</td>
            <td>{String(integrationSyncConfig.syncAllCards)}</td>
          </tr>
          <tr>
            <td>Use admin roles</td>
            <td>{String(integrationSyncConfig.setAdminFromHR)}</td>
          </tr>
          <tr>
            <td>Create subteams from Departments</td>
            <td>{String(integrationSyncConfig.isDepartments)}</td>
          </tr>
          <tr>
            <td>Create subteams from email domains</td>
            <td>{String(integrationSyncConfig.isEmailDomainGroups)}</td>
          </tr>
          <tr>
            <td>Reassign leads to</td>
            <td>{String(integrationSyncConfig.defaultLeadAssignee)}</td>
          </tr>
        </tbody>
      </table>
      <h4>AD groups to sync</h4>
      <h5>Total Members: {totalMembers}</h5>
      <h5 style={{ marginBottom: 5 }}>Search member</h5>
      <div className={classes.searchContainer}>
        <TextField
          style={{ width: 400 }}
          variant="outlined"
          label="User Principal Name"
          onChange={(e) => setSearchMember(e.target.value?.trim())}
          disabled={isSearching}
          value={searchMember}
          error={searchMemberErrorText !== ""}
          helperText={searchMemberErrorText}
        />
        <div style={{ width: 50, margin: "14px 0 0 30px" }} className="buttonContainerMini" onClick={handleSearch}>{isSearching ? <CircularProgress color="secondary" size={15} /> : "Search"}</div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Group name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isFetchingGroups && <CircularProgress />}
          {
            adGroups?.map(({ displayName, id }) => (
              <tr key={id}>
                <td>{displayName}</td>
                <td>
                  <div
                    className="buttonContainerMini"
                    onClick={() => setGroupModalOpen({ isOpen: true, data: { groupId: id, displayName } })}
                    style={{ width: 100, margin: "0 auto" }}
                  >
                    View
                  </div>
                </td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <ViewGroupModal
        connection={selectedConnection}
        open={!!groupModalOpen.isOpen}
        onClose={() => setGroupModalOpen({ isOpen: false, data: {} })}
        setGroupModalOpen={setGroupModalOpen}
        {...groupModalOpen.data}
      />
      <ViewMemberModal open={searchMemberData.user} onClose={handleCloseMemberModal} memberData={searchMemberData} />
    </div>
  );
}

export default Overview;
